/**
 * useCmsArticlesIndex — published articles (raw index entries), fresher than
 * the bundle. Mirrors `useCmsLectures`: the build-time snapshot seeds state,
 * `/api/cms/public/articles?locale=` replaces it on mount.
 *
 * Unlike `useCmsArticles` (which resolves titles per locale and unions tags),
 * this returns the raw `CmsArticleIndexEntry[]` so callers that need the
 * per-locale `title_*` / `excerpt_*` columns (e.g. NewsTeaser) can pick them
 * themselves with the same fallback chain the server uses.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cmsArticles, type CmsArticleIndexEntry } from '@/content/cms/articles.generated'
import { previewTokenParam } from '@/utils/previewToken'
import { DEFAULT_LOCALE, type Locale } from '@/utils/localePath'
import { useCmsData } from '@/cms/CmsDataContext'

interface ArticlesResp {
  articles?: CmsArticleIndexEntry[]
}

export function useCmsArticlesIndex(): CmsArticleIndexEntry[] {
  const { i18n } = useTranslation()
  const locale = (i18n.language || DEFAULT_LOCALE) as Locale
  const cmsData = useCmsData()
  const initial = cmsData?.articles ?? cmsArticles
  const [items, setItems] = useState<CmsArticleIndexEntry[]>(initial)

  useEffect(() => {
    if (cmsData?.articles) return
    const controller = new AbortController()
    let cancelled = false

    void fetch(
      `/api/cms/public/articles?locale=${encodeURIComponent(locale)}&limit=200${previewTokenParam()}`,
      { signal: controller.signal, credentials: 'same-origin' },
    )
      .then((r) => (r.ok ? (r.json() as Promise<ArticlesResp>) : null))
      .then((j) => {
        if (cancelled) return
        if (!j?.articles?.length) return
        setItems(j.articles)
      })
      .catch(() => {
        // Network error / abort — keep the snapshot seed.
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [locale, cmsData?.articles])

  return items
}
